export interface CanvasDrawingOptions {
  readonly strokeStyle?: string;
  readonly lineWidth?: number;
  readonly lineCap?: CanvasLineCap;
  readonly lineJoin?: CanvasLineJoin;
}

export interface CanvasDrawingCallbacks {
  readonly onDrawEnd?: () => void;
}

interface DrawingState {
  isDrawing: boolean;
  lastX: number;
  lastY: number;
}

const DEFAULT_OPTIONS: Required<CanvasDrawingOptions> = {
  strokeStyle: '#000000',
  lineWidth: 2,
  lineCap: 'round',
  lineJoin: 'round',
};

export interface CanvasDrawingHandle {
  destroy(): void;
  clear(): void;
}

export function attachCanvasDrawing(
  canvas: HTMLCanvasElement,
  callbacks?: CanvasDrawingCallbacks,
  options?: CanvasDrawingOptions,
): CanvasDrawingHandle | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  const merged = { ...DEFAULT_OPTIONS, ...options };
  ctx.strokeStyle = merged.strokeStyle;
  ctx.lineWidth = merged.lineWidth;
  ctx.lineCap = merged.lineCap;
  ctx.lineJoin = merged.lineJoin;

  const state: DrawingState = { isDrawing: false, lastX: 0, lastY: 0 };

  function onPointerStart(x: number, y: number): void {
    state.isDrawing = true;
    state.lastX = x;
    state.lastY = y;
  }

  function onPointerMove(x: number, y: number): void {
    if (!state.isDrawing) {
      return;
    }
    ctx!.beginPath();
    ctx!.moveTo(state.lastX, state.lastY);
    ctx!.lineTo(x, y);
    ctx!.stroke();
    state.lastX = x;
    state.lastY = y;
  }

  function onPointerEnd(): void {
    if (!state.isDrawing) {
      return;
    }
    state.isDrawing = false;
    callbacks?.onDrawEnd?.();
  }

  function getTouchCoords(e: TouchEvent): { x: number; y: number } | null {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    if (!touch) {
      return null;
    }
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }

  function handleMouseDown(e: MouseEvent): void { onPointerStart(e.offsetX, e.offsetY); }
  function handleMouseMove(e: MouseEvent): void { onPointerMove(e.offsetX, e.offsetY); }
  function handleMouseUp(): void { onPointerEnd(); }
  function handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const coords = getTouchCoords(e);
    if (coords) { onPointerStart(coords.x, coords.y); }
  }
  function handleTouchMove(e: TouchEvent): void {
    e.preventDefault();
    const coords = getTouchCoords(e);
    if (coords) { onPointerMove(coords.x, coords.y); }
  }
  function handleTouchEnd(): void { onPointerEnd(); }

  canvas.addEventListener('mousedown', handleMouseDown);
  canvas.addEventListener('mousemove', handleMouseMove);
  canvas.addEventListener('mouseup', handleMouseUp);
  canvas.addEventListener('mouseleave', handleMouseUp);
  canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
  canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
  canvas.addEventListener('touchend', handleTouchEnd);

  return {
    destroy(): void {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    },
    clear(): void {
      ctx!.clearRect(0, 0, canvas.width, canvas.height);
    },
  };
}
